import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Building2, Settings2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import OrgChartSetup from '../components/org/OrgChartSetup';

// All Modules under Strategy Wolves with unique title gradients
const allModules = [
  {
    id: 'project-management',
    coloredWord: 'Project',
    restOfName: 'Management',
    description: 'Manage projects, tasks, timelines, and team collaboration',
    available: true,
    path: '/dashboard',
    titleGradient: 'from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9]', // Purple
    gradient: 'from-violet-600/20 via-purple-600/10 to-transparent',
    shadowColor: 'shadow-violet-500/20',
    hoverShadow: 'hover:shadow-violet-500/40'
  },
  {
    id: 'portfolio-management',
    coloredWord: 'Portfolio',
    restOfName: 'Management',
    description: 'Oversee project portfolios, track PM updates, and monitor escalations',
    available: true,
    path: '/portfolio',
    titleGradient: 'from-[#EC4899] via-[#DB2777] to-[#BE185D]', // Pink
    gradient: 'from-fuchsia-600/20 via-pink-600/10 to-transparent',
    shadowColor: 'shadow-fuchsia-500/20',
    hoverShadow: 'hover:shadow-fuchsia-500/40'
  },
  {
    id: 'action-items',
    coloredWord: 'Action',
    restOfName: 'Items',
    description: 'Track and manage action items across projects',
    available: true,
    path: '/action-items',
    titleGradient: 'from-[#F59E0B] via-[#D97706] to-[#B45309]', // Amber/Orange
    gradient: 'from-amber-600/20 via-orange-600/10 to-transparent',
    shadowColor: 'shadow-amber-500/20',
    hoverShadow: 'hover:shadow-amber-500/40'
  },
  {
    id: 'strategy-development',
    coloredWord: 'Strategy',
    restOfName: 'Development',
    description: 'AI-guided strategic planning journey with document generation',
    available: true,
    path: '/strategy-development',
    titleGradient: 'from-[#06B6D4] via-[#0891B2] to-[#0E7490]', // Cyan (swapped from Meeting)
    gradient: 'from-cyan-600/20 via-sky-600/10 to-transparent',
    shadowColor: 'shadow-cyan-500/20',
    hoverShadow: 'hover:shadow-cyan-500/40'
  },
  {
    id: 'strategy-execution',
    coloredWord: 'Strategy',
    restOfName: 'Execution',
    description: 'Balanced Scorecard performance management & KPI tracking',
    available: true,
    path: '/strategy',
    titleGradient: 'from-[#14B8A6] via-[#0D9488] to-[#0F766E]', // Teal
    gradient: 'from-teal-600/20 via-cyan-600/10 to-transparent',
    shadowColor: 'shadow-teal-500/20',
    hoverShadow: 'hover:shadow-teal-500/40'
  },
  {
    id: 'meeting-management',
    coloredWord: 'Meeting',
    restOfName: 'Management',
    description: 'Schedule, track, and manage meetings effectively',
    available: true,
    path: '/meetings',
    titleGradient: 'from-[#F97316] via-[#EA580C] to-[#C2410C]', // Orange (swapped from Strategy Dev)
    gradient: 'from-orange-600/20 via-amber-600/10 to-transparent',
    shadowColor: 'shadow-orange-500/20',
    hoverShadow: 'hover:shadow-orange-500/40'
  }
];

// Module Card Component with unique gradient
const ModuleCard = ({ module, onClick }) => (
  <div
    onClick={() => onClick(module)}
    data-testid={`module-${module.id}`}
    className={`
      relative group rounded-2xl p-6 overflow-hidden
      bg-[#0f1729]/60 backdrop-blur-sm
      border border-blue-500/10
      transition-all duration-300 ease-out
      ${module.available 
        ? `cursor-pointer hover:scale-[1.02] hover:border-blue-500/30 shadow-lg ${module.shadowColor} ${module.hoverShadow}` 
        : 'cursor-not-allowed opacity-60'
      }
    `}
  >
    {/* Unique gradient background for each card */}
    <div 
      className={`absolute inset-0 bg-gradient-to-br ${module.gradient} opacity-50 group-hover:opacity-70 transition-opacity duration-500`}
    />

    {/* Lock overlay for unavailable modules */}
    {!module.available && (
      <div className="absolute top-3 right-3 z-10">
        <Lock className="w-4 h-4 text-blue-400/50" />
      </div>
    )}

    {/* Content */}
    <div className="relative z-10">
      {/* Module Name with unique colored gradient */}
      <h3 className="text-xl font-semibold mb-3">
        <span className={`bg-gradient-to-r ${module.titleGradient} bg-clip-text text-transparent`}>
          {module.coloredWord}
        </span>
        <span className="text-white/90 ml-1.5">{module.restOfName}</span>
      </h3>
      
      {/* Description */}
      <p className="text-sm text-blue-300/50 leading-relaxed">
        {module.description}
      </p>

      {/* Status badge */}
      {!module.available && (
        <div className="mt-4">
          <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400/70 border border-blue-500/20">
            Coming Soon
          </span>
        </div>
      )}
    </div>
  </div>
);

const ModuleSelector = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { organization } = useOrg();
  const [showOrgSetup, setShowOrgSetup] = useState(false);

  const handleModuleClick = (module) => {
    if (module.available && module.path) {
      navigate(module.path);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMGMTStackClick = () => {
    logout(); // Log out from Strategy Wolves session
    // Use window.location for hard navigation to avoid router redirects
    window.location.href = '/mgmt-login';
  };

  return (
    <div className="min-h-screen flex flex-col items-center relative overflow-x-hidden bg-[#080d14] p-6 pt-20">
      {/* Top Right Controls - MGMT Stack Button + Logout */}
      <div className="absolute top-6 right-6 z-20 flex items-center gap-3">
        {/* MGMT Stack Button */}
        <button
          onClick={handleMGMTStackClick}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6]/10 to-[#EC4899]/10 border border-purple-500/20 text-white/80 hover:text-white hover:border-purple-500/40 hover:from-[#8B5CF6]/20 hover:to-[#EC4899]/20 transition-all duration-200 group"
          data-testid="mgmt-stack-btn"
        >
          <img 
            src="/mgmt-logo.png" 
            alt="MGMT Stack" 
            className="w-7 h-7 object-contain group-hover:scale-110 transition-transform"
          />
          <span className="text-sm font-medium">
            <span className="bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] bg-clip-text text-transparent">MGMT</span>
            <span className="text-white/80 ml-1">Stack</span>
          </span>
        </button>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Logout</span>
        </button>
      </div>

      {/* Animated Glowing Background Orbs - Colorful theme */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Orb 1 - Pink */}
        <div 
          className="absolute w-96 h-96 rounded-full blur-3xl"
          style={{ 
            background: 'radial-gradient(circle, #EC4899 0%, transparent 70%)',
            animation: 'glowMove1 12s ease-in-out infinite'
          }}
        />
        
        {/* Orb 2 - Orange */}
        <div 
          className="absolute w-80 h-80 rounded-full blur-3xl"
          style={{ 
            background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)',
            animation: 'glowMove2 15s ease-in-out infinite'
          }}
        />
        
        {/* Orb 3 - Purple Center - Large */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl"
          style={{ 
            background: 'radial-gradient(circle, #8B5CF6 0%, transparent 60%)',
            animation: 'glowMove3 18s ease-in-out infinite'
          }}
        />
        
        {/* Orb 4 - Magenta accent */}
        <div 
          className="absolute w-72 h-72 rounded-full blur-3xl"
          style={{ 
            background: 'radial-gradient(circle, #DB2777 0%, transparent 70%)',
            animation: 'glowMove4 10s ease-in-out infinite'
          }}
        />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(236, 72, 153, 0.3) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes glowMove1 {
          0% { 
            top: 15%; 
            left: 10%; 
            opacity: 0.25;
          }
          25% { 
            opacity: 0.08;
          }
          50% { 
            top: 60%; 
            left: 70%; 
            opacity: 0.3;
          }
          75% { 
            opacity: 0.08;
          }
          100% { 
            top: 15%; 
            left: 10%; 
            opacity: 0.25;
          }
        }
        
        @keyframes glowMove2 {
          0% { 
            bottom: 20%; 
            right: 15%; 
            opacity: 0.2;
          }
          25% { 
            opacity: 0.05;
          }
          50% { 
            bottom: 50%; 
            right: 60%; 
            opacity: 0.25;
          }
          75% { 
            opacity: 0.05;
          }
          100% { 
            bottom: 20%; 
            right: 15%; 
            opacity: 0.2;
          }
        }
        
        @keyframes glowMove3 {
          0% { 
            top: 50%; 
            left: 50%; 
            transform: translate(-50%, -50%);
            opacity: 0.15;
          }
          33% { 
            top: 30%; 
            left: 30%; 
            transform: translate(-50%, -50%);
            opacity: 0.05;
          }
          66% { 
            top: 70%; 
            left: 70%; 
            transform: translate(-50%, -50%);
            opacity: 0.2;
          }
          100% { 
            top: 50%; 
            left: 50%; 
            transform: translate(-50%, -50%);
            opacity: 0.15;
          }
        }
        
        @keyframes glowMove4 {
          0% { 
            top: 10%; 
            right: 20%; 
            opacity: 0.18;
          }
          33% { 
            opacity: 0.05;
          }
          50% { 
            top: 70%; 
            right: 10%; 
            opacity: 0.22;
          }
          66% { 
            opacity: 0.05;
          }
          100% { 
            top: 10%; 
            right: 20%; 
            opacity: 0.18;
          }
        }
      `}</style>

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-2xl font-semibold text-white/90 mb-2">Select a Module</h1>
          <p className="text-blue-300/50 text-sm">Choose a module to get started</p>
        </div>

        {/* Strategy Wolves Section - All Modules */}
        <div className="mb-8">
          {/* Strategy Wolves Brand - Left aligned */}
          <div className="flex flex-row items-center gap-3 mb-6 pl-1">
            <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
              <img 
                src="/logos/logo-circle-wolf.png" 
                alt="Strategy Wolves" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <div className="h-12 w-px bg-white/70 shadow-[0_0_10px_rgba(255,255,255,0.8),0_0_20px_rgba(255,255,255,0.4)] flex-shrink-0" />
            <h2 className="text-3xl font-bold tracking-tight whitespace-nowrap">
              <span className="bg-gradient-to-r from-[#7A8A99] via-[#96A4AF] to-[#B0BBC4] bg-clip-text text-transparent">
                Strategy
              </span>
              <span className="text-white/90 ml-2">Wolves</span>
            </h2>
          </div>

          {/* All Modules Grid - 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {allModules.map((module) => (
              <ModuleCard key={module.id} module={module} onClick={handleModuleClick} />
            ))}
          </div>
        </div>

        {/* Footer - 2026 Strategy Wolves - Full width across */}
        <div className="w-full border-t border-blue-500/10 mt-10 pt-6">
          <div className="flex items-center justify-between">
            <p className="text-blue-400/40 text-xs tracking-wider">
              &copy; 2026 Strategy Wolves. All rights reserved.
            </p>
            <button
              onClick={() => setShowOrgSetup(true)}
              data-testid="org-setup-btn"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:text-white/80 hover:bg-white/10 transition-all duration-200 text-xs"
            >
              <Settings2 className="w-3.5 h-3.5" />
              {organization ? 'Edit Organization' : 'Company Setup'}
            </button>
          </div>
        </div>
      </div>

      {/* Organization Setup - Visual Org Chart */}
      <OrgChartSetup isOpen={showOrgSetup} onClose={() => setShowOrgSetup(false)} />
    </div>
  );
};

export default ModuleSelector;
